import { useAirbnbUrl, isRealAirbnbUrl } from "@/hooks/useAirbnbUrl";

interface Props {
  accommodationId: number;
  price: string;
  rating: number;
  reviewCount: number;
  airbnbUrl: string;
  hostName: string;
}

export default function DetailBookingCard({ accommodationId, price, rating, reviewCount, airbnbUrl, hostName }: Props) {
  const resolvedUrl = useAirbnbUrl(accommodationId, airbnbUrl);
  const hasRealUrl = isRealAirbnbUrl(resolvedUrl);

  return (
    <>
      {/* 데스크탑 사이드 카드 */}
      <div className="hidden lg:block w-full lg:w-[340px] shrink-0 self-start lg:sticky lg:top-24">
        <BookingCardContent price={price} rating={rating} reviewCount={reviewCount} airbnbUrl={resolvedUrl} hasRealUrl={hasRealUrl} hostName={hostName} />
      </div>

      {/* 모바일 하단 고정 바 */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-stone-200 px-4 py-3 flex items-center gap-3">
        <div className="flex-1">
          <span className="text-lg font-bold text-stone-900">{price}</span>
          <span className="text-xs text-stone-400 ml-1">/박</span>
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-amber-400 text-xs"></i>
            <span className="text-xs font-semibold text-stone-700">{rating}</span>
            <span className="text-stone-400 text-xs">· 후기 {reviewCount}개</span>
          </div>
        </div>
        {hasRealUrl ? (
          <a
            href={resolvedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-external-link-line text-sm"></i>
            에어비앤비 예약
          </a>
        ) : (
          <div className="flex flex-col items-center gap-0.5 bg-stone-100 px-4 py-2.5 rounded-xl">
            <span className="text-xs font-semibold text-stone-500 whitespace-nowrap">링크 준비중</span>
            <span className="text-[10px] text-stone-400 whitespace-nowrap">곧 연동 예정</span>
          </div>
        )}
      </div>

      {/* 모바일 하단 고정 바 높이만큼 여백 */}
      <div className="lg:hidden h-20" />
    </>
  );
}

interface CardContentProps {
  price: string;
  rating: number;
  reviewCount: number;
  airbnbUrl: string;
  hasRealUrl: boolean;
  hostName: string;
}

function BookingCardContent({ price, rating, reviewCount, airbnbUrl, hasRealUrl, hostName }: CardContentProps) {
  return (
    <div>
      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        {/* Price */}
        <div className="mb-5">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-stone-900">{price}</span>
            <span className="text-stone-400 text-sm">/박</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-amber-400 text-sm"></i>
            </div>
            <span className="text-sm font-semibold text-stone-700">{rating}</span>
            <span className="text-stone-400 text-sm">· 후기 {reviewCount}개</span>
          </div>
        </div>

        {/* CTA */}
        {hasRealUrl ? (
          <a
            href={airbnbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-amber-500 hover:bg-amber-600 text-white text-center font-bold py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap mb-3"
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-external-link-line text-base"></i>
              에어비앤비에서 예약하기
            </span>
          </a>
        ) : (
          <div className="w-full bg-stone-100 rounded-xl py-3.5 mb-3 text-center">
            <span className="flex items-center justify-center gap-2 text-stone-400 font-semibold text-sm">
              <i className="ri-link-unlink text-base"></i>
              에어비앤비 링크 연동 준비중
            </span>
            <p className="text-xs text-stone-400 mt-1">아래 카카오톡으로 예약 문의해 주세요</p>
          </div>
        )}

        <p className="text-xs text-stone-400 text-center mb-5">에어비앤비 공식 결제 시스템으로 안전하게 예약됩니다</p>

        {/* Divider */}
        <div className="border-t border-stone-100 mb-5" />

        {/* Host contact */}
        <div className="mb-4">
          <p className="text-sm font-semibold text-stone-700 mb-3">예약 전 궁금한 점이 있으신가요?</p>
          <div className="flex flex-col gap-2.5">
            {/* 카카오톡 */}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full bg-[#FEE500] hover:bg-[#f0d800] text-[#3A1D1D] font-semibold text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/e/e3/KakaoTalk_logo.svg"
                alt="카카오톡"
                className="w-5 h-5 object-contain"
              />
              카카오톡으로 문의하기
            </a>
            {/* 라인 */}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full bg-[#06C755] hover:bg-[#05b34c] text-white font-semibold text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/4/41/LINE_logo.svg"
                alt="라인"
                className="w-5 h-5 object-contain"
              />
              라인으로 문의하기
            </a>
          </div>
          <p className="text-xs text-stone-400 text-center mt-2">{hostName} 호스트가 직접 답변드립니다</p>
        </div>

        {/* Info list */}
        <div className="space-y-2.5">
          {[
            { icon: "ri-translate-2", text: "한국어로 모든 소통 가능" },
            { icon: "ri-shield-check-line", text: "에어비앤비 호스트 인증 완료" },
            { icon: "ri-time-line", text: "24시간 이내 응답 보장" },
            { icon: "ri-refund-2-line", text: "에어비앤비 환불 정책 적용" },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2.5 text-sm text-stone-600">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-amber-500 text-sm`}></i>
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Share / Save */}
      <div className="flex gap-2 mt-3">
        <button className="flex-1 flex items-center justify-center gap-2 text-sm text-stone-600 hover:text-amber-600 border border-stone-200 hover:border-amber-300 rounded-xl py-2.5 transition-all cursor-pointer whitespace-nowrap bg-white">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-heart-line text-sm"></i>
          </div>
          저장
        </button>
        <button
          onClick={() => navigator.clipboard.writeText(window.location.href)}
          className="flex-1 flex items-center justify-center gap-2 text-sm text-stone-600 hover:text-amber-600 border border-stone-200 hover:border-amber-300 rounded-xl py-2.5 transition-all cursor-pointer whitespace-nowrap bg-white"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-share-line text-sm"></i>
          </div>
          공유
        </button>
      </div>
    </div>
  );
}
